// TripScreens.tsx
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import {
   Breadcrumb,
   Button,
   Card,
   Col,
   Divider,
   Input,
   Row,
   Select,
   Table,
   Typography,
   DatePicker,
   Tooltip,
   Modal,
   Pagination
} from 'antd'
import {
   HomeOutlined,
   InfoCircleOutlined,
   DeleteOutlined,
   ExclamationCircleOutlined,
   SearchOutlined,
   CheckCircleOutlined,
   CloseCircleOutlined,
   SwapOutlined,
   PlusCircleOutlined
} from '@ant-design/icons'
import { useHistory } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { ROUTER_PATH } from '../routes/metaData'
import useModal from 'src/helpers/modal/useModal'
import { formatCurrency } from 'src/helpers/functions'
import { useStore } from 'src/hooks'
import { useLoading } from 'src/common/components/LoadingContainer'
import TextArea from 'antd/lib/input/TextArea'
import { debounce, isEmpty } from 'lodash'
import { getNextOrderStatuses, LIST_ORDER_STATUS, PAGE_SIZE, statusOrder } from 'src/helpers/constants'

const { Option } = Select
const { Title } = Typography
const { RangePicker } = DatePicker

const TripScreen = observer(() => {
   const history = useHistory()
   const { t } = useTranslation()
   const { confirm } = useModal()
   const [formSearch, setFormSearch] = useState<any>({})
   const OrderStore = useStore('OrderStore')
   const UserStore = useStore(`UserStore`)
   const { showLoading, hideLoading } = useLoading()
   const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
   const [currentPage, setCurrentPage] = useState<number>(1)
   const [totalRecords, setTotalRecords] = useState<number>(0)
   const [cancelReason, setCancelReason] = useState<string>('')

   const getInint = async () => {
      await UserStore.getEmployees()
      await OrderStore.getRoutes()
   }

   // FETCH DATA
   const fetchData = useCallback(
      async (page: number = 1, searchParams: any = {}) => {
         try {
            showLoading()
            const params: any = {
               limit: PAGE_SIZE,
               offset: (page - 1) * PAGE_SIZE
            }

            // Thêm các tham số tìm kiếm
            if (searchParams.search) {
               params.search = searchParams.search
            }
            if (searchParams.driver) {
               params.driverId = searchParams.driver
            }
            if (searchParams.route) {
               params.routeId = searchParams.route
            }
            if (searchParams.status !== undefined && searchParams.status !== null) {
               params.status = searchParams.status
            }
            if (searchParams.date && searchParams.date.length === 2) {
               params.startDate = searchParams.date[0].format('YYYY-MM-DD')
               params.endDate = searchParams.date[1].format('YYYY-MM-DD')
            }

            const result = await OrderStore.getOrders(params)
            // Lấy total từ API response
            setTotalRecords(
               result?.totalResults || result?.count || OrderStore.listOrder.count || OrderStore.listOrder.rows.length
            )
         } catch (error) {
            console.log('fetch data order error : ', error)
         } finally {
            hideLoading()
         }
      },
      [OrderStore, showLoading, hideLoading]
   )

   useEffect(() => {
      getInint()
      fetchData(1, formSearch)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   // CREATE BUTTON
   const handleRedirectToCreate = useCallback(() => {
      history.push(`/${ROUTER_PATH.TripCreateScreen}`)
   }, [history])

   // GET DETAIL
   const showDetail = useCallback(
      (record: any) => {
         history.push(`/${ROUTER_PATH.TripDetailScreen.replace(':id', record?.id?.toString())}`)
      },
      [history]
   )

   // ACTION BUTTON
   const handleAction = useCallback(
      (record: any, type: string) => {
         switch (type) {
            case `delete`:
               handleDeleteTrip(record.id)
               break
            case `transfer`:
               OrderStore.selectedStatus = getNextOrderStatuses(OrderStore.selectedStatus)[0]?.value
               OrderStore.typeModal = 'changeStatus'
               OrderStore.openModal = true
               if (OrderStore.entries.length === 0) {
                  OrderStore.idAction = record.id
                  const findOrder = OrderStore.findTripById(record.id) as any
                  if (findOrder) OrderStore.selectedStatus = findOrder.status
               }
               break
            case `complete`:
               handleComplete(record.id)
               break
            case `cancel`:
               OrderStore.typeModal = 'cancel'
               OrderStore.openModal = true
               if (OrderStore.entries.length === 0) {
                  OrderStore.idAction = record.id
               }
               break
            case `details`:
               showDetail(record)
               break
            default:
               break
         }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [confirm, t]
   )

   // DELETE ACTION
   const handleDeleteTrip = async (id: number) => {
      confirm({
         title: `Bạn muốn xoá chuyến`,
         icon: <ExclamationCircleOutlined />,
         okText: t('agree'),
         cancelText: t('cancel'),
         async onOk() {
            showLoading()
            if (OrderStore.entries.length === 0) {
               await OrderStore.deleteTrip(id)
            } else {
               await Promise.all(OrderStore.entries.map((element: any) => OrderStore.deleteTrip(element)))
            }
            setSelectedRowKeys([])
            OrderStore.entries = []
            hideLoading()
            fetchData(currentPage, formSearch)
         }
      })
   }

   // CHANGE STATUS ACTION
   const handleChangeStatus = async () => {
      showLoading()
      if (OrderStore.entries.length === 0) {
         OrderStore.idAction && (await OrderStore.changeStatusTrip(OrderStore.idAction))
      } else {
         await Promise.all(OrderStore.entries.map((element: any) => OrderStore.changeStatusTrip(element)))
      }
      setSelectedRowKeys([])
      OrderStore.entries = []
      hideLoading()
      fetchData(currentPage, formSearch)
   }

   // COMPLETE ACTION
   const handleComplete = async (id: number) => {
      confirm({
         title: `Bạn muốn đánh dấu hoàn thành chuyến`,
         icon: <ExclamationCircleOutlined />,
         okText: t('agree'),
         cancelText: t('cancel'),
         async onOk() {
            showLoading()
            if (OrderStore.entries.length === 0) {
               await OrderStore.completeTrip(id)
            } else {
               await Promise.all(OrderStore.entries.map((element: any) => OrderStore.completeTrip(element)))
            }
            setSelectedRowKeys([])
            OrderStore.entries = []
            hideLoading()
            fetchData(currentPage, formSearch)
         }
      })
   }

   // CANCEL ACTION
   const handleCancel = async () => {
      showLoading()
      if (OrderStore.entries.length === 0) {
         OrderStore.idAction && (await OrderStore.cancelTrip(OrderStore.idAction, cancelReason))
      } else {
         await Promise.all(OrderStore.entries.map((element: any) => OrderStore.cancelTrip(element, cancelReason)))
      }
      setSelectedRowKeys([])
      OrderStore.entries = []
      setCancelReason('')
      hideLoading()
      fetchData(currentPage, formSearch)
   }

   //PAGINATION
   const handlePageChange = (page: number) => {
      setCurrentPage(page)
      fetchData(page, formSearch)
   }

   const renderPagination = () => {
      if (isEmpty(OrderStore.listOrder.rows)) return null

      return (
         <div className="containerPagination">
            <Pagination
               current={currentPage}
               pageSize={PAGE_SIZE}
               total={totalRecords}
               onChange={handlePageChange}
               showSizeChanger={false}
            />
         </div>
      )
   }

   // ACTION MODAL
   const renderModal = (type: 'cancel' | 'changeStatus') => {
      return (
         <Modal
            title={type === 'changeStatus' ? 'Đổi trạng thái chuyến đi' : 'Huỷ chuyến'}
            visible={OrderStore.openModal}
            okText={t('agree')}
            cancelText={t('cancel')}
            onOk={async () => {
               if (type === 'changeStatus' && OrderStore.selectedStatus) {
                  await handleChangeStatus()
               }
               if (type === 'cancel') {
                  await handleCancel()
               }
               OrderStore.openModal = false
               OrderStore.typeModal = null
            }}
            onCancel={() => {
               OrderStore.openModal = false
               OrderStore.typeModal = null
            }}
         >
            {type === 'changeStatus' && (
               <>
                  <p>Chọn trạng thái chuyến đi</p>
                  <Select
                     value={getNextOrderStatuses(OrderStore.selectedStatus)[0]?.value}
                     onChange={(value: number) => {
                        OrderStore.selectedStatus = value
                     }}
                     className="w-100"
                     style={{ width: '200px' }}
                  >
                     {getNextOrderStatuses(OrderStore.selectedStatus).map((status) => (
                        <Option key={status.value} value={status.value}>
                           {status.label}
                        </Option>
                     ))}
                  </Select>
               </>
            )}
            {type === 'cancel' && (
               <>
                  <p>Nhập lý do huỷ chuyến :</p>
                  <TextArea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
               </>
            )}
         </Modal>
      )
   }

   // TABLE DATA
   const columns = useMemo(
      () => [
         { title: 'Mã chuyến', dataIndex: 'id', key: 'id' },
         {
            title: 'SĐT người đặt',
            dataIndex: 'customer',
            key: 'customer',
            render: (customer: any, record: any) => customer?.phoneNumber || record?.customerName
         },
         {
            title: 'Người đặt',
            dataIndex: 'customer',
            key: 'customer',
            render: (customer: any, record: any) => customer?.name || record?.customerPhone
         },
         {
            title: 'Tuyến đường',
            dataIndex: 'routeId',
            key: 'routeId',
            render: (routeId: number) => OrderStore.getRouteName(routeId)
         },
         {
            title: 'Ngày đặt',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => new Date(date).toLocaleString('vi-VN')
         },
         {
            title: 'Ngày đi',
            dataIndex: 'departureDateTime',
            key: 'departureDateTime',
            render: (date: string) => new Date(date).toLocaleString('vi-VN')
         },
         {
            title: 'Ghi chú',
            dataIndex: 'packageDescription',
            key: 'packageDescription',
            render: (description: string) => description || '-'
         },
         {
            title: 'Tổng tiền',
            dataIndex: 'routeId',
            key: 'routeId',
            render: (routeId: number, record: any) => OrderStore.totalPrice(routeId, record.orderType)
         },
         {
            title: 'Hướng đi',
            dataIndex: 'tripDirection',
            key: 'tripDirection',
            render: (tripDirection: number) =>
               tripDirection === 1 ? 'Chiều đi' : tripDirection === 2 ? 'Chiều về' : 'Khứ hồi cùng ngày'
         },
         {
            title: 'Loại dịch vụ',
            dataIndex: 'orderType',
            key: 'orderType',
            render: (orderType: number) => OrderStore.renderOrderType(orderType)
         },
         {
            title: 'Hoa hồng',
            dataIndex: 'commission',
            key: 'commission',
            render: (value: number) => formatCurrency(value)
         },
         {
            title: 'Người bán chuyến',
            dataIndex: 'commissionReceiver',
            key: 'commissionReceiver',
            render: (commissionReceiver: any) => commissionReceiver?.name || '-'
         },
         { title: 'Tài xế', dataIndex: 'driver', key: 'driver', render: (driver: any) => driver?.name || '-' },
         {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            render: (status: number) => OrderStore.renderStatus(status)
         },
         {
            title: 'Hành động',
            key: 'action',
            width: 150,
            className: 'action-column',
            fixed: 'right' as 'right',
            render: (record: any) => (
               <span style={{ display: 'flex', flexDirection: 'row' }}>
                  <span onClick={() => handleAction(record, `details`)} className="gx-link">
                     <Tooltip placement="top" title={'Chi tiết'}>
                        <InfoCircleOutlined />
                     </Tooltip>
                  </span>
                  {!(
                     record.status === statusOrder.CANCELLED ||
                     record.status === statusOrder.REFUNDED ||
                     record.status === statusOrder.COMPLETED
                  ) && (
                     <>
                        <Divider type="vertical" />
                        <span onClick={() => handleAction(record, 'transfer')} className="gx-link">
                           <Tooltip placement="top" title={'Chuyển'}>
                              <SwapOutlined />
                           </Tooltip>
                        </span>
                     </>
                  )}

                  {!(
                     record.status === statusOrder.CANCELLED ||
                     record.status === statusOrder.REFUNDED ||
                     record.status === statusOrder.COMPLETED
                  ) && (
                     <>
                        <Divider type="vertical" />
                        <span onClick={() => handleAction(record, 'complete')} className="gx-link">
                           <Tooltip placement="top" title={'Hoàn thành chuyến'}>
                              <CheckCircleOutlined />
                           </Tooltip>
                        </span>
                     </>
                  )}

                  {!(
                     record.status === statusOrder.CANCELLED ||
                     record.status === statusOrder.REFUNDED ||
                     record.status === statusOrder.COMPLETED
                  ) && (
                     <>
                        <Divider type="vertical" />
                        <span onClick={() => handleAction(record, 'cancel')} className="gx-link">
                           <Tooltip placement="top" title={'Huỷ chuyến'}>
                              <CloseCircleOutlined />
                           </Tooltip>
                        </span>
                     </>
                  )}
               </span>
            )
         }
      ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [showDetail, handleAction]
   )

   // SEARCH TRIP
   const onSearch = () => {
      setCurrentPage(1)
      fetchData(1, formSearch)
   }

   return (
      <div>
         <Row style={{ padding: '0 20px' }} className="gx-justify-content-between gx-align-items-center">
            <Col>
               <Title level={4}>{'Quản lý chuyến'}</Title>
               <Breadcrumb>
                  <Breadcrumb.Item href="/">
                     <HomeOutlined className="gx-link" />
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>{'Admin'}</Breadcrumb.Item>
                  <Breadcrumb.Item className="gx-link">{'Quản lý chuyến'}</Breadcrumb.Item>
               </Breadcrumb>
            </Col>
            <Col>
               <Button icon={<PlusCircleOutlined />} type="primary" onClick={handleRedirectToCreate}>
                  {t('add_new')}
               </Button>
            </Col>
         </Row>

         <Card style={{ marginTop: '20px' }}>
            {/* SEARCH FORM */}
            <Row gutter={16}>
               <Col span={6}>
                  <Input
                     placeholder={'Tên, SĐT, ghi chú'}
                     allowClear
                     onChange={(e) => setFormSearch({ ...formSearch, search: e.target.value })}
                  />
               </Col>
               <Col span={4}>
                  <Select
                     showSearch
                     placeholder={'Tài xế'}
                     style={{ width: '100%' }}
                     allowClear
                     onChange={(v) => setFormSearch({ ...formSearch, driver: v })}
                     options={OrderStore.driverOptions(UserStore.listEmployees.rows)}
                  ></Select>
               </Col>
               <Col span={4}>
                  <Select
                     showSearch
                     placeholder={'Tuyến đường'}
                     style={{ width: '100%' }}
                     allowClear
                     onChange={(v) => setFormSearch({ ...formSearch, route: v })}
                     options={OrderStore.routerOptions()}
                     filterOption={(input, option) =>
                        String(option?.label ?? '')
                           .toLowerCase()
                           .includes(input.toLowerCase())
                     }
                  ></Select>
               </Col>
               <Col span={4}>
                  <Select
                     placeholder={'Trạng thái'}
                     style={{ width: '100%' }}
                     allowClear
                     onChange={(v) => setFormSearch({ ...formSearch, status: v })}
                  >
                     {LIST_ORDER_STATUS.map((status) => (
                        <Option key={status.value} value={status.value}>
                           {status.label}
                        </Option>
                     ))}
                  </Select>
               </Col>
               <Col span={6}>
                  <RangePicker style={{ width: '100%' }} onChange={(e) => setFormSearch({ ...formSearch, date: e })} />
               </Col>
               <Col span={4} style={{ marginTop: 8 }}>
                  <Button icon={<SearchOutlined />} type="primary" onClick={debounce(onSearch, 600)}>
                     {t('search')}
                  </Button>
               </Col>
            </Row>

            {/* TABLE */}
            <Table
               rowSelection={{
                  type: 'checkbox',
                  selectedRowKeys,
                  onChange: (key, value) => {
                     setSelectedRowKeys(key)
                     OrderStore.entries = key
                  }
               }}
               style={{ marginTop: 20 }}
               columns={columns}
               dataSource={OrderStore.listOrder.rows}
               rowKey="id"
               pagination={false}
               scroll={{ x: 'max-content' }}
            />
            {renderPagination()}
         </Card>

         {/* MODAL ACTION */}
         {OrderStore.typeModal && renderModal(OrderStore.typeModal)}
      </div>
   )
})

export default TripScreen
